import { z } from 'zod';
import { UuidSchema, VersionDetailSchema, type VersionApprovalStatus } from './apps.js';
import { DimensionSchema, MetricSchema, SECTION_KEYS, SectionKeySchema } from './catalog.js';

/*
 * Gerar contexto (fase 3): o Lastrouter lê o modelo do app na fonte, como o admin logado, e monta
 * uma PROPOSTA de contexto com os achados de risco. A proposta fica guardada com a execução; só entra
 * num rascunho quando uma pessoa aplica as partes que quiser.
 */

export const FindingSeveritySchema = z.enum(['critico', 'alto', 'medio', 'baixo']);
export type FindingSeverity = z.infer<typeof FindingSeveritySchema>;

/** Risco encontrado no modelo, por regra determinística. */
export const ContextFindingSchema = z.object({
  /** Regra que encontrou (rotulo_ambiguo, chave_sintetica...). */
  rule: z.string(),
  /** Único na execução: regra + item. */
  id: z.string(),
  severity: FindingSeveritySchema,
  title: z.string(),
  /** O que foi visto no modelo, uma linha por evidência. */
  evidence: z.array(z.string()),
  /** O que o agente faria de errado sem tratar o risco. */
  impact: z.string(),
  recommendation: z.string(),
  /** Regra para o agente que este risco pede, quando pede: vira linha da seção restricoes da proposta. */
  agent_rule: z.string().nullable(),
  /** Aceito no painel: quem, quando e o motivo. Risco crítico aceito não trava a certificação. */
  accepted: z.object({ by: z.string(), at: z.string(), reason: z.string() }).nullable().optional(),
});
export type ContextFinding = z.infer<typeof ContextFindingSchema>;

/** Item sugerido com a origem: de onde no modelo ele saiu. */
const Origin = { origem: z.string() };
export const SuggestedMetricSchema = MetricSchema.extend(Origin);
export type SuggestedMetric = z.infer<typeof SuggestedMetricSchema>;
export const SuggestedDimensionSchema = DimensionSchema.safeExtend(Origin);
export type SuggestedDimension = z.infer<typeof SuggestedDimensionSchema>;

export const ContextSuggestionSchema = z.object({
  metricas: z.array(SuggestedMetricSchema),
  dimensoes: z.array(SuggestedDimensionSchema),
  secoes: z.partialRecord(SectionKeySchema, z.string().max(50_000)),
  /** De onde saiu o texto de cada seção. */
  origem_secoes: z.partialRecord(SectionKeySchema, z.string()),
});
export type ContextSuggestion = z.infer<typeof ContextSuggestionSchema>;

export const ContextRunStatusSchema = z.enum(['rodando', 'ok', 'falhou', 'interrompida']);
export type ContextRunStatus = z.infer<typeof ContextRunStatusSchema>;

export const ContextRunStepSchema = z.object({
  name: z.string(),
  status: z.enum(['rodando', 'ok', 'falhou']),
  started_at: z.string(),
  finished_at: z.string().nullable(),
  detail: z.string().nullable(),
  /** Andamento do passo que roda caso a caso (bateria): feitos de total. */
  progress: z.object({ done: z.number().int().min(0), total: z.number().int().min(0) }).optional(),
});
export type ContextRunStep = z.infer<typeof ContextRunStepSchema>;

export const ContextRunOptionsSchema = z.object({
  /** Campo usado como dimensão com mais valores distintos que isto vira achado. */
  alta_cardinalidade: z.number().int().min(100).max(100_000_000).default(10_000),
  /** O que quem gera sabe do app (siglas, regras, o que fica de fora). Vai para a LLM da interpretação como evidência. */
  notas: z.string().trim().max(4000).optional(),
  /** Quantas imagens vieram com as notas; ficam nos arquivos da execução (notas/imagem-N). */
  imagens: z.number().int().min(0).max(4).optional(),
});

/** Imagem que acompanha as notas: print de tela ou foto de documento, já reduzida pelo painel. */
export const ContextRunImageSchema = z
  .string()
  .max(4_000_000)
  .regex(/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/]+=*$/, 'imagem PNG, JPEG ou WebP em data URL');
export type ContextRunOptions = z.infer<typeof ContextRunOptionsSchema>;

export const ContextRunSchema = z.object({
  id: UuidSchema,
  tenant_id: z.string(),
  asset_id: UuidSchema,
  status: ContextRunStatusSchema,
  /** Passo atual (ou o último). */
  step: z.string().nullable(),
  steps: z.array(ContextRunStepSchema),
  options: ContextRunOptionsSchema,
  error: z.string().nullable(),
  started_by: z.string(),
  started_at: z.string(),
  finished_at: z.string().nullable(),
});
export type ContextRun = z.infer<typeof ContextRunSchema>;

/**
 * Arquivos de uma execução, guardados com ela. Os do pipeline (ADR 0044) têm os nomes do protótipo; planilhas vão em
 * base64 (.b64), porque o repositório guarda texto.
 */
export const CONTEXT_RUN_FILES = {
  model: 'modelo.json',
  findings: 'achados.json',
  suggestion: 'sugestao.json',
  snapshot: 'snapshot.json',
  script: 'script.qvs',
  plan: 'engine_plan.json',
  results: 'engine_results.json',
  semantics: 'semantica_llm.json',
  context: 'contexto.json',
  battery: 'bateria.json',
  batteryPlan: 'bateria_plan.json',
  batteryResults: 'bateria_results.json',
  validation: 'validacao_cliente.json',
  answers: 'respostas_agente.json',
  /** Respostas da bateria que está rodando, gravadas a cada caso: o painel mostra o andamento. */
  batteryProgress: 'bateria_progresso.json',
  evaluation: 'avaliacao.json',
  certificationConfig: 'certificacao_config.json',
  ossie: 'contexto.ossie.yaml',
  workbook: 'checkpoint.xlsx.b64',
  vocabulary: 'vocabulario_qlik_answers.xlsx.b64',
  outOfProposal: 'fora_da_proposta.json',
} as const;

/** Arquivos que o painel deixa baixar, pelo nome público. */
export const CONTEXT_RUN_DOWNLOADS = ['contexto.json', 'contexto.ossie.yaml', 'checkpoint.xlsx', 'vocabulario_qlik_answers.xlsx', 'bateria.json', 'avaliacao.json', 'snapshot.json'] as const;
export const ContextRunDownloadSchema = z.enum(CONTEXT_RUN_DOWNLOADS);
export type ContextRunDownload = z.infer<typeof ContextRunDownloadSchema>;

const ConditionSchema = z.object({ condition: z.string(), ok: z.boolean(), detail: z.string() });

/** Resumo do pipeline para o painel; os arquivos inteiros ficam com a execução. */
export const PipelineSummarySchema = z.object({
  risks: z.object({ critical: z.number().int(), high: z.number().int(), medium: z.number().int(), low: z.number().int() }),
  questions: z.number().int(),
  answered_questions: z.number().int(),
  catalog: z.object({ expose: z.number().int(), review: z.number().int(), hide: z.number().int() }),
  quality: z.object({ today: z.number(), with_context: z.number() }).nullable(),
  /** Situação dos números: pronto para piloto, piloto com ressalvas, corrigir antes do piloto... */
  numbers: z.string().nullable(),
  battery: z.object({ cases: z.number().int(), critical: z.number().int(), by_status: z.record(z.string(), z.number().int()), engine_checks: z.number().int() }).nullable(),
  certification: z
    .object({ status: z.string(), score: z.number().nullable(), critical_score: z.number().nullable(), conditions: z.array(ConditionSchema), context_version: z.string().nullable() })
    .nullable(),
  interpretation: z.object({ items: z.number().int(), model: z.string(), removed_evidence: z.number().int(), rewrites: z.number().int() }).nullable(),
  /** Itens visíveis que não entraram na proposta, com o motivo (fórmula com variável, dimensão calculada). */
  out_of_proposal: z.array(z.object({ item: z.string(), reason: z.string() })),
  files: z.array(ContextRunDownloadSchema),
});
export type PipelineSummary = z.infer<typeof PipelineSummarySchema>;

/** Selo da certificação que libera o contexto para produção (texto que o pipeline grava). */
export const CERTIFIED_STATUS = 'liberado para produção';

/**
 * O que falta para publicar com validação (ADR 0050): aprovações dos donos e a certificação da última execução do
 * pipeline. `certification` undefined: o app não tem execução do pipeline. O painel mostra a lista antes da confirmação
 * e o serviço grava a mesma lista na versão, por isso a regra mora aqui.
 */
export function unvalidatedPending(approvals: VersionApprovalStatus['pending'], certification: PipelineSummary['certification'] | undefined): string[] {
  const out = approvals.map((p) => `Aprovação do dono ${p.papel === 'negocio' ? 'de negócio' : 'técnico'} (${p.owner})`);
  if (certification === undefined) out.push('Sem geração pelo pipeline: a bateria não rodou');
  else if (certification === null) out.push('Certificação ainda não avaliada');
  else if (certification.status !== CERTIFIED_STATUS) {
    const failing = certification.conditions.filter((c) => !c.ok);
    out.push(...(failing.length ? failing.map((c) => `${c.condition}: ${c.detail}`) : [`Certificação: ${certification.status}`]));
  }
  return out;
}

export const ContextRunDetailSchema = z.object({
  run: ContextRunSchema,
  findings: z.array(ContextFindingSchema).nullable(),
  suggestion: ContextSuggestionSchema.nullable(),
  /** Contagens do modelo lido (o modelo inteiro fica no arquivo da execução). */
  model: z.object({ title: z.string(), tables: z.number().int(), fields: z.number().int(), measures: z.number().int(), dimensions: z.number().int(), sheets: z.number().int(), script: z.boolean() }).nullable(),
  /** Execução feita pelo pipeline de contexto (ADR 0044); null nas gerações antigas. */
  pipeline: PipelineSummarySchema.nullable().default(null),
});
export type ContextRunDetail = z.infer<typeof ContextRunDetailSchema>;

/** Arquivo para baixar: nome, tipo e conteúdo em base64. */
export const ContextRunFileSchema = z.object({ name: ContextRunDownloadSchema, content_type: z.string(), base64: z.string() });

/** Validação do cliente (passo 6): a planilha preenchida, em base64. Até 10 MB. */
export const ImportValidationInputSchema = z.object({ xlsx_base64: z.string().min(1).max(14_000_000) });

/** Item do gabarito da bateria: a pergunta e a fórmula do Qlik que responde (nunca um número). */
export const AnswerKeyItemSchema = z.object({
  /** Id do teste no arquivo (T01); só para achar a linha, não muda o caso. */
  id: z.string().trim().max(40).optional(),
  categoria: z.string().trim().max(60).optional(),
  pergunta: z.string().trim().min(1).max(500),
  formula: z.string().trim().min(1).max(8000),
  critico: z.boolean().default(false),
});
export type AnswerKeyItem = z.infer<typeof AnswerKeyItemSchema>;
/** Formato do arquivo de gabarito (docs/gabarito.md). */
export const ANSWER_KEY_FORMAT = 'lastrouter.gabarito/1';
export const AnswerKeySchema = z.object({
  items: z.array(AnswerKeyItemSchema).max(1000),
  /** Data em que o gabarito foi montado (AAAA-MM-DD). */
  gerado_em: z.string().trim().max(40).nullable().default(null),
  /** Regras de fórmula que valem para o app inteiro (ex.: {1} ignora as seleções). */
  convencoes: z.array(z.string().trim().min(1).max(1000)).max(50).default([]),
});
/** Gabarito para ler: uma planilha (base64) ou um texto JSON (lista de {pergunta, resposta}). */
export const AnswerKeyParseInputSchema = z.union([z.object({ xlsx_base64: z.string().min(1).max(14_000_000) }), z.object({ json: z.string().min(1).max(5_000_000) })]);
export const AnswerKeyParsedSchema = AnswerKeySchema.extend({ warnings: z.array(z.string()) });

/** Uma pergunta da bateria na tela: a fórmula esperada (nunca o valor), o que o agente respondeu e o resultado. */
export const BatteryRowSchema = z.object({
  id: z.string(),
  categoria: z.string().nullable(),
  critico: z.boolean(),
  pergunta: z.string(),
  /** Fórmula do gabarito; null nos casos que a geração criou a partir do catálogo. */
  formula: z.string().nullable(),
  /** Fórmula que o agente usou (o que o Qlik executou, com os filtros); null se não consultou. Nunca o resultado. */
  formula_agente: z.string().nullable(),
  /** A fórmula do agente é a mesma do gabarito, sem contar espaços, caixa e comentários. */
  mesma_formula: z.boolean().nullable(),
  /** aguardando: ainda não perguntado; respondido: perguntado, a nota sai no fim da rodada. */
  resultado: z.enum(['certo', 'errado', 'sem resposta', 'não avaliado', 'respondido', 'aguardando']),
  motivo: z.string(),
  /** Em relação à rodada anterior do app. */
  tendencia: z.enum(['melhorou', 'piorou', 'igual']).nullable().optional(),
});
export const BatteryResultsSchema = z.object({
  running: z.boolean(),
  done: z.number().int(),
  total: z.number().int(),
  /** Quando a última rodada terminou; null se a bateria nunca rodou. */
  evaluated_at: z.string().nullable(),
  score: z.number().nullable(),
  critical_score: z.number().nullable(),
  rows: z.array(BatteryRowSchema),
});
export type BatteryResults = z.infer<typeof BatteryResultsSchema>;

/**
 * Sugestão de contexto que sai de uma pergunta errada da bateria. metrica: entra no catálogo com a fórmula do gabarito
 * (nunca outra); secao: uma linha nova numa seção (sinônimo, regra, exemplo). Só vale depois de aprovada e publicada.
 */
export const BatterySuggestionSchema = z.object({
  caso: z.string(),
  pergunta: z.string(),
  motivo: z.string(),
  /** consulta: a fórmula do gabarito vira métrica e a pergunta vira consulta validada (verificada por construção). */
  tipo: z.enum(['consulta', 'metrica', 'secao']),
  metrica: z.object({ nome: z.string().min(1).max(120), expressao: z.string().min(1).max(8000), sinonimos: z.array(z.string().min(1).max(200)).max(10) }).optional(),
  /** Outras formas de fazer a mesma pergunta, para a consulta validada. */
  exemplos: z.array(z.string().min(1).max(200)).max(10).optional(),
  /** A fórmula que entra é a do gabarito: certa por construção, pode ser aplicada sem revisão. */
  verificada: z.boolean().optional(),
  secao: SectionKeySchema.optional(),
  texto: z.string().max(800).optional(),
  /** aplicada: foi para o rascunho; publicada: entrou no ar; recusada: descartada; revertida: desfeita. Sem o campo: não decidida. */
  estado: z.enum(['aplicada', 'publicada', 'recusada', 'revertida']).optional(),
  /** O que a aplicação criou, para desfazer. */
  aplicado: z.object({ metrica_id: z.string().optional(), query_id: z.string().optional(), secao: SectionKeySchema.optional() }).optional(),
});
export type BatterySuggestion = z.infer<typeof BatterySuggestionSchema>;

/** Uma rodada da bateria guardada: dá para voltar a ela depois, com as perguntas, as fórmulas e as sugestões. */
export const BatteryArchiveSummarySchema = z.object({
  run_id: UuidSchema,
  id: z.string(),
  started_at: z.string(),
  finished_at: z.string().nullable(),
  status: z.enum(['concluida', 'cancelada', 'falhou']),
  score: z.number().nullable(),
  right: z.number().int(),
  wrong: z.number().int(),
  total: z.number().int(),
  suggestions: z.number().int(),
  /** Perguntas que mudaram em relação à rodada anterior. */
  improved: z.number().int().default(0),
  regressed: z.number().int().default(0),
  /** Uma letra por pergunta, na ordem: c certa, e errada, a não feita. Desenha o mini-gráfico da lista de rodadas. */
  marks: z.string().max(2000).optional(),
  /** Tokens da LLM na rodada (agente, juiz e sugestões), como o provedor contou. */
  tokens: z.object({ input: z.number().int(), output: z.number().int(), calls: z.number().int() }).optional(),
  /** Rodada do Aprender sozinho: ciclo N de M. */
  cycle: z.number().int().optional(),
  cycles: z.number().int().optional(),
});
export type BatteryArchiveSummary = z.infer<typeof BatteryArchiveSummarySchema>;
export const BatteryArchiveSchema = BatteryArchiveSummarySchema.extend({
  critical_score: z.number().nullable(),
  rows: z.array(BatteryRowSchema),
  sugestoes: z.array(BatterySuggestionSchema),
});
export type BatteryArchive = z.infer<typeof BatteryArchiveSchema>;
export const ApplyBatterySuggestionsInputSchema = z.object({
  run_id: UuidSchema,
  battery_id: z.string().min(1).max(40),
  indexes: z.array(z.number().int().min(0)).min(1).max(200),
  expected_updated_at: z.string(),
});

/** Bateria contra o agente (passo 7): quantas tentativas por caso (a configuração do cliente decide o mínimo). */
export const RunBatteryInputSchema = z.object({
  attempts: z.number().int().min(1).max(5).optional(),
  /** Aprender sozinho: depois da rodada, aplica ao rascunho as sugestões verificadas e roda de novo, até 3 ciclos ou 80%. */
  learn: z.boolean().default(false),
});

/** Parte da proposta a aplicar: as listas da camada semântica, todas as seções, ou uma seção. */
export const ApplyPartSchema = z.enum(['metricas', 'dimensoes', 'secoes', ...SECTION_KEYS.map((k) => `secoes:${k}` as const)]);
export type ApplyPart = z.infer<typeof ApplyPartSchema>;

export const ApplyRunInputSchema = z.object({
  run_id: UuidSchema,
  parts: z.array(ApplyPartSchema).min(1),
  /** substituir: a parte do rascunho vira a da proposta. acrescentar: entra só o que falta (itens com id novo; texto ao fim da seção). */
  mode: z.enum(['substituir', 'acrescentar']),
  expected_updated_at: z.string(),
});
export type ApplyRunInput = z.infer<typeof ApplyRunInputSchema>;

const UuidParam = z.object({ id: UuidSchema });

export const CONTEXT_RUNS_ROUTES = {
  startContextRun: {
    method: 'POST',
    path: '/assets/:id/context-runs',
    access: 'admin',
    summary: 'Gera uma proposta de contexto lendo o modelo do app na fonte como o admin logado',
    params: UuidParam,
    body: z.object({ options: ContextRunOptionsSchema.default({ alta_cardinalidade: 10_000 }), images: z.array(ContextRunImageSchema).max(4).default([]) }),
    response: ContextRunSchema,
  },
  contextRuns: { method: 'GET', path: '/assets/:id/context-runs', access: 'owner', summary: 'Últimas 20 gerações de contexto do app', params: UuidParam, response: z.array(ContextRunSchema) },
  contextRun: { method: 'GET', path: '/context-runs/:id', access: 'owner', summary: 'Uma geração com passos, achados e a proposta', params: UuidParam, response: ContextRunDetailSchema },
  contextRunFile: {
    method: 'GET',
    path: '/context-runs/:id/files/:name',
    access: 'owner',
    summary: 'Baixa um arquivo da execução do pipeline (contexto, Ossie, planilha, vocabulário, bateria, avaliação)',
    params: z.object({ id: UuidSchema, name: ContextRunDownloadSchema }),
    response: ContextRunFileSchema,
  },
  importContextRunValidation: {
    method: 'POST',
    path: '/context-runs/:id/validation',
    access: 'owner',
    summary: 'Importa a planilha preenchida pelo cliente e refaz contexto, bateria e certificação (passo 6)',
    params: UuidParam,
    body: ImportValidationInputSchema,
    response: ContextRunDetailSchema,
  },
  answerKey: {
    method: 'GET',
    path: '/context-runs/:id/answer-key',
    access: 'owner',
    summary: 'Gabarito da bateria: perguntas e fórmulas',
    params: UuidParam,
    response: AnswerKeySchema,
  },
  parseAnswerKey: {
    method: 'POST',
    path: '/answer-key/parse',
    access: 'owner',
    summary: 'Lê um gabarito de planilha (colunas Pergunta e Resposta) ou de JSON, sem gravar',
    body: AnswerKeyParseInputSchema,
    response: AnswerKeyParsedSchema,
  },
  saveAnswerKey: {
    method: 'PUT',
    path: '/context-runs/:id/answer-key',
    access: 'owner',
    summary: 'Grava o gabarito, calcula as fórmulas novas na engine (como quem salvou) e refaz bateria e certificação',
    params: UuidParam,
    body: AnswerKeySchema,
    response: ContextRunDetailSchema,
  },
  batteryResults: {
    method: 'GET',
    path: '/context-runs/:id/battery-results',
    access: 'owner',
    summary: 'Perguntas da bateria com o valor esperado, a resposta do agente e o resultado; durante a rodada, o andamento',
    params: UuidParam,
    response: BatteryResultsSchema,
  },
  assetBatteries: {
    method: 'GET',
    path: '/assets/:id/batteries',
    access: 'owner',
    summary: 'Rodadas da bateria do app, da mais nova para a mais antiga',
    params: UuidParam,
    response: z.array(BatteryArchiveSummarySchema),
  },
  battery: {
    method: 'GET',
    path: '/context-runs/:id/batteries/:battery',
    access: 'owner',
    summary: 'Uma rodada da bateria guardada: perguntas, fórmulas, resultado e sugestões de contexto',
    params: z.object({ id: UuidSchema, battery: z.string().min(1).max(40) }),
    response: BatteryArchiveSchema,
  },
  acceptRisk: {
    method: 'POST',
    path: '/context-runs/:id/risks/:risk',
    access: 'owner',
    summary: 'Aceita um risco com motivo (ou reabre); a certificação é refeita na hora e o aceite passa para as próximas gerações',
    params: z.object({ id: UuidSchema, risk: z.string().min(1).max(40) }),
    body: z.object({ accepted: z.boolean(), reason: z.string().trim().max(2000).default('') }),
    response: z.array(ContextFindingSchema),
  },
  refuseBatterySuggestions: {
    method: 'POST',
    path: '/context-runs/:id/batteries/:battery/refuse',
    access: 'owner',
    summary: 'Recusa sugestões de uma rodada da bateria (ficam marcadas, não vão para o rascunho)',
    params: z.object({ id: UuidSchema, battery: z.string().min(1).max(40) }),
    body: z.object({ indexes: z.array(z.number().int().min(0)).min(1).max(200) }),
    response: BatteryArchiveSchema,
  },
  publishBatterySuggestion: {
    method: 'POST',
    path: '/context-runs/:id/batteries/:battery/publish',
    access: 'admin',
    summary: 'Aplica uma sugestão da bateria e publica na hora, direto (motivo automático e auditoria)',
    params: z.object({ id: UuidSchema, battery: z.string().min(1).max(40) }),
    body: z.object({ index: z.number().int().min(0) }),
    response: BatteryArchiveSchema,
  },
  revertBatterySuggestion: {
    method: 'POST',
    path: '/context-runs/:id/batteries/:battery/revert',
    access: 'admin',
    summary: 'Desfaz uma sugestão aplicada ou publicada (tira do rascunho e, se estava no ar, publica a volta)',
    params: z.object({ id: UuidSchema, battery: z.string().min(1).max(40) }),
    body: z.object({ index: z.number().int().min(0) }),
    response: BatteryArchiveSchema,
  },
  applyBatterySuggestions: {
    method: 'POST',
    path: '/versions/:id/battery-suggestions',
    access: 'owner',
    summary: 'Leva sugestões de uma rodada da bateria ao rascunho (zera as aprovações e registra o que mudou)',
    params: UuidParam,
    body: ApplyBatterySuggestionsInputSchema,
    response: VersionDetailSchema,
  },
  cancelContextRun: {
    method: 'POST',
    path: '/context-runs/:id/cancel',
    access: 'admin',
    summary: 'Cancela a geração ou a bateria que está rodando; o que já foi gravado fica, e a execução sai como interrompida',
    params: UuidParam,
    response: ContextRunDetailSchema,
  },
  runContextRunBattery: {
    method: 'POST',
    path: '/context-runs/:id/battery',
    access: 'admin',
    summary: 'Roda a bateria contra o agente com o contexto publicado e avalia (passos 7 e 8)',
    params: UuidParam,
    body: RunBatteryInputSchema,
    response: ContextRunDetailSchema,
  },
  applyContextRun: {
    method: 'POST',
    path: '/versions/:id/apply-run',
    access: 'owner',
    summary: 'Aplica partes da proposta gerada ao rascunho (zera as aprovações e registra o que mudou)',
    params: UuidParam,
    body: ApplyRunInputSchema,
    response: VersionDetailSchema,
  },
} as const;
